
/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { MailBroadcaster } from "../../../common/providers/mail-broadcaster.service";
import { getActiveProfile, getUserProfile, RootState, getFederatedApps, getAllCalendarAppointments, getCalendarFolders } from "src/app/reducers";
import { Store } from "@ngrx/store";
import { getContactById, getAllUserContacts } from "../../../reducers/index";
import { Utils } from "src/app/common/utils";
import { ConfigService } from "src/app/config.service";
import { switchMap, takeUntil, Subject, filter, take, of, debounce, debounceTime } from "rxjs";
import { MailUtils } from "src/app/mail/utils/mail-utils";
import { ElectronService } from "src/app/services/electron.service";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { SetActiveProfile, SetLastPhotoUpdate, SetRightSidebarStatus, SetUserProfile, StopProcessing } from "src/app/actions/app";
import { ToastService } from "src/app/common/providers/toast.service";
import { CommonRepository } from "src/app/mail/repositories/common-repository";
import { UserProfile } from "../../models";
import { RandomColor } from "src/app/common/utils/random-color";
import { MailService } from "src/app/mail/shared/services/mail-service";
import { CommonUtils } from "src/app/common/utils/common-util";
import { META_TASK_MENTION, TICKET_MENTION } from "@uxf/vnc-library";
import { DomSanitizer } from "@angular/platform-browser";
import { ContactRootState } from "src/app/contacts/store";
import { environment } from "src/environments/environment";
import { CommonService } from "src/app/services/ common.service.";
import { CalendarAppointment, CalendarFolder } from "src/app/common/models/calendar.model";
import * as _ from "lodash";
import * as moment from "moment";
import { MailConstants } from "src/app/common/utils/mail-constants";
import { DatePipe, registerLocaleData } from "@angular/common";
import { CalenderUtils } from "src/app/calendar/utils/calender-utils";
import { MailTag } from "src/app/mail/models/mail-tag.model";
import localeDE from "@angular/common/locales/de";
import localEN from "@angular/common/locales/en";
import localRU from "@angular/common/locales/ru";
import { isEqual, isAfter, isBefore, startOfDay } from "date-fns";
import format from "date-fns/format";
import { AppService } from "src/app/services/app.service";
import { MatDialog } from "@angular/material/dialog";
import { AvatarUploadDialogComponent } from "../avatar-upload-dialog/avatar-upload.component";
import { FormControl } from "@angular/forms";

@Component({
  selector: "vp-right-sidebar",
  templateUrl: "./right-sidebar.component.html",
  styleUrls: [
    "./right-sidebar.component.scss"
  ]
})
export class RightSidebarComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private breakpointObserver: BreakpointObserver,
    private electronService: ElectronService,
    private mailService: MailService,
    private store: Store<RootState>,
    private toastService: ToastService,
    private commonRepository: CommonRepository,
    private translate: TranslateService,
    private config: ConfigService,
    private changeDetectionRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private datepipe: DatePipe,
    private mailBroadCaster: MailBroadcaster,
    private commonService: CommonService,
    public appStore: Store<ContactRootState>,
    private appService: AppService,
    private matDialog: MatDialog,
  ) {
      this.isFluid = this.appService.isInsideIframe();
      this.commonRepository.rightSidebarClose$.pipe(takeUntil(this.isAlive$)).subscribe(() => {
        this.closeSidebar();
      });
    const currentName = localStorage.getItem("theme") || environment.theme;
    if (currentName === "dfb") {
      this.currentTheme = "dfb";
    } else if (currentName === "carbon") {
      this.currentTheme = "carbon";
    } else {
      this.currentTheme = "default";
    }
    this.isLargeScreen = this.breakpointObserver.isMatched("(min-width: 1480px)");
    this.breakpointObserver
      .observe("(min-width: 1480px)").pipe(takeUntil(this.isAlive$))
      .subscribe((state: BreakpointState) => {
        this.isLargeScreen = state.matches;
      });
    this.config.currentLanguage.asObservable().pipe(switchMap(lang => this.translate.getTranslation(lang)), takeUntil(this.isAlive$))
      .subscribe(v => {
        this.translations = v;
      });

      this.appStore.select(getUserProfile).pipe(filter(v => !!v)).subscribe(res => {
        this.userJID = res;
      });

      this.commonRepository.getZimbraTagsList().pipe(takeUntil(this.isAlive$)).subscribe(allTags => {
        this.allZimbraTags = allTags;
      });
      this.mailBroadCaster.on<any>("UPDATE_SIDEBAR_APPOINTMENTS").pipe(takeUntil(this.isAlive$)).subscribe(v => {
        if (this.calendarAppointments.length > 0) {
          const today = startOfDay(new Date());
          this.calendarAppointments = this.calendarAppointments.filter(appointment => {
            const start = new Date(appointment.start);
            const end = new Date(appointment.end);
            return (
              isEqual(start, today) || isAfter(start, today) ||
              (isBefore(start, today) && isAfter(end, today))
            );
          });
          this.calendarAppointments.sort((a, b) => {
            return new Date(a.start).getTime() - new Date(b.start).getTime();
          });
          this.mobileGroupingEvents(this.calendarAppointments);
          // console.log("reminder sidebar updated calendarAppointments: ", this.calendarAppointments);
          this.changeDetectionRef.markForCheck();
        }
      });
      this.store.select(getAllCalendarAppointments)
        .pipe(takeUntil(this.isAlive$))
        .subscribe((calendarAppointments: CalendarAppointment[]) => {
          if (!!calendarAppointments) {
            this.calendarAppointments = calendarAppointments;
            const today = startOfDay(new Date());
            this.calendarAppointments = calendarAppointments.filter(appointment => {
              const start = new Date(appointment.start);
              const end = new Date(appointment.end);
              return (
                isEqual(start, today) || isAfter(start, today) ||
                (isBefore(start, today) && isAfter(end, today))
              );
            });
            this.calendarAppointments.sort((a, b) => {
                return new Date(a.start).getTime() - new Date(b.start).getTime();
            });
            this.mobileGroupingEvents(this.calendarAppointments);
            this.changeDetectionRef.markForCheck();
          }
      });
      this.store.select(getCalendarFolders).pipe(takeUntil(this.isAlive$)).subscribe(res => {
          if (!!res && res.length > 0) {
              this.calendarFolders = res;
              this.changeDetectionRef.markForCheck();
          }
      });
      this.config.currentLanguage.pipe(takeUntil(this.isAlive$)).subscribe(res => {
        if (!!res) {
          switch (res) {
            case "de": this.locale = "de"; registerLocaleData(localeDE); break;
            case "ru": this.locale = "ru"; registerLocaleData(localRU); break;
            default: this.locale = "en"; registerLocaleData(localEN);
          }
        } else {
          registerLocaleData(localEN);
        }
        this.changeDetectionRef.markForCheck();
      });

  }

  get fullName(): string {
    return [this.contact.first_name, this.contact.last_name].filter(v => !!v).join(" ");
  }
  isFluid: boolean = false;
  calendarFolders: CalendarFolder[] = [];
  allZimbraTags: MailTag[] = [];
  calendarAppointments: CalendarAppointment[] = [];
  locale = "en";
  mobileEvents: any = null;
  contact: any;
  currentTheme: string = "default";
  translations: any = {};
  private isAlive$ = new Subject<boolean>();
  isLargeScreen: boolean;
  currentUser: UserProfile;
  isLoggedInUser: any;
  supportedApps = ["talk", "mail", "task"];
  searchParams: any = {
    q: "*:*",
    sort: "created_dt desc",
    start: 0,
    apps: this.supportedApps.join(","),
    limit: 20
  };
  searchResponse: any;
  isLoading: boolean = true;
  activeEmail: string = "";
  headerTitle: string = "";
  allContacts: any[] = [];
  emojiOnly: boolean = false;
  userJID: any = {};
  isSearchResult: boolean;
  hasMention: boolean;
  actorName: string;
  isMyMessage: boolean;
  totalParticipants: any;
  fromJid: string;
  translationKey: string;
  fullActorName: string;
  isMeeting: boolean;
  keyword: any;
  isShowTalkOptions: boolean = false;
  isShowTaskOption: boolean = false;
  isShowTicketOption: boolean = false;
  searchControl: FormControl = new FormControl();
  activeIndex: number = 0;
  otherUserName: string = "";

  recussingEventPreventHitForGetparticipentsList: any[] = [];
  havemultipleparticipants: any[] = [];

  ngOnInit(): void {
    this.store.select(getUserProfile).pipe(filter(v => !!v), takeUntil(this.isAlive$)).subscribe(res => {
      if (!!res) {
        this.currentUser = res;
      }
    });
    this.store.select(getFederatedApps).pipe(filter(apps => !!apps && apps.length > 0), takeUntil(this.isAlive$)).subscribe(apps => {
      const isTalkAppAvailable = apps.filter(fa => fa.name === "vnctalk")[0];
      if (!!isTalkAppAvailable) {
        this.isShowTalkOptions = true;
      } else {
        this.isShowTalkOptions = false;
      }
      const isTaskAppAvailable = apps.filter(fa => fa.name === "vnctask")[0];
      if (!!isTaskAppAvailable) {
        this.isShowTaskOption = true;
      } else {
        this.isShowTaskOption = false;
      }
      const isProjectAppAvailable = apps.filter(fa => fa.name === "vncproject")[0];
      if (!!isProjectAppAvailable) {
        this.isShowTicketOption = true;
      } else {
        this.isShowTicketOption = false;
      }
      this.changeDetectionRef.markForCheck();
    });
    this.store.select(getActiveProfile).pipe(switchMap(email => {
      console.log("getActiveProfile", email);
      this.activeEmail = email;
      if (this.activeEmail) {
        this.headerTitle = "PROFILE";
      } else {
        this.headerTitle = "UPCOMING_APPOINTMENTS";
      }
      this.changeDetectionRef.markForCheck();
      this.searchResponse = [];
      if (!!email) {
        this.contact = {
          email: this.activeEmail,
          first_name: this.activeEmail.split("@")[0]
        };
        this.contact.firstLastCharacters = Utils.getCharacters(this.activeEmail.split("@")[0], "");
        if (this.activeIndex === 0) this.callSearchActivity("talk,mail");
        else if (this.activeIndex === 1) this.callSearchActivity("talk");
        else if (this.activeIndex === 2) this.callSearchActivity("mail");
        if (this.currentUser.email === email) {
          return this.mailService.getLoggedInUserContactInfo(this.currentUser.email);
        }
        return this.store.select(state => getContactById(state, email)).pipe(takeUntil(this.isAlive$));
      } else {
        this.contact = {};
        return of(null);
      }
    }), takeUntil(this.isAlive$)).subscribe(contact => {
      // console.log("rightsidebar contact0: ", contact);
      if (!!contact) {
        if (!contact.email && contact.jid) {
          contact.email = contact.jid;
        } else if (!contact.email && contact.emails) {
          contact.email = contact.emails[0].email;
        }
        this.contact = contact;
        console.log("rightsidebar contact: ", contact);
        if (this.activeIndex === 0) this.callSearchActivity("talk,mail");
        else if (this.activeIndex === 1) this.callSearchActivity("talk");
        else if (this.activeIndex === 2) this.callSearchActivity("mail");
        this.contact.firstLastCharacters = Utils.getCharacters(contact.first_name, contact.last_name);
      } else {
        this.store.select(state => getAllUserContacts(state)).pipe(take(1)).subscribe(res => {
          // console.log("rightsidebar getAllUserContacts: ", res);
          const filtered = res.filter(c => c.email === this.activeEmail);
          // console.log("rightsidebar getAllUserContacts filtered: ", filtered[0]);
          if (filtered.length > 0) {
            this.contact = {
              email: this.activeEmail,
              first_name: filtered[0].fileAsStr,
            };
            if (!!filtered[0]._attrs && !!filtered[0]._attrs.jid && (filtered[0]._attrs.jid.indexOf("@") > -1)) {
              this.contact.jid = filtered[0]._attrs.jid;
            }
            this.contact.firstLastCharacters = Utils.getCharacters(filtered[0].first_name, filtered[0].last_name);
            // console.log("rightsidebar contact3: ", this.contact);
          }

          if (!this.contact.jid) {
            this.mailService.getCheckHistory(this.activeEmail).pipe(take(1)).subscribe(historyres => {
              console.log("rightsidebar contact post history: ", historyres);
              if (!!historyres && !!historyres.found && (historyres.found > 0)) {
                this.contact.jid = this.activeEmail;
              }
            });
          }
        });
      }
    });
    this.searchControl.valueChanges.pipe(debounceTime(300), takeUntil(this.isAlive$)).subscribe(val => {
      this.isLoading = true;
      if (this.activeIndex === 0) this.callSearchActivity("talk,mail", val);
      else if (this.activeIndex === 1) this.callSearchActivity("talk", val);
      else if (this.activeIndex === 2) this.callSearchActivity("mail", val);
    });
  }

  getBgAvatarColor(jid) {
    return RandomColor.getCharColor(jid);
  }

  getAvatarUrl(jid) {
    return this.contact.avatar;
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.isAlive$.next(false);
    this.isAlive$.complete();
  }

  copyEmail(email: string) {
    console.log("copyEmail", email);
    MailUtils.copyToClipboard([email]);
    this.toastService.show("COPIED_TO_CLIPBOARD");
  }

  openURL(url: string) {
    if (typeof cordova !== "undefined") {
      if (device.platform === "iOS") {
        window.open(url, "_system");
      } else if (device.platform === "Android") {
        navigator.app.loadUrl(url, {
          openExternal: true
        });
      }
    } else if (this.electronService.isElectron) {
      this.electronService.openExternalUrl(url);
    } else {
      window.open(url, "_blank");
    }
  }

  closeSidebar() {
    this.store.dispatch(new SetRightSidebarStatus(false));
    this.store.dispatch(new SetActiveProfile(null));
  }

  handleProfileAction(action) {
    switch (action) {
      case "audio": this.startAudioCall(); break;
      case "video": this.startVideoCall(); break;
      case "chat": this.startChat(); break;
      case "mail": this.composeEmail(); break;
      case "ticket": this.createTicket(); break;
      case "task": this.createTask(); break;
    }
  }

  composeEmail(): void {
    this.commonRepository.sendEmail(this.contact.email);
  }

  createTask(): void {
    this.commonRepository.createTask(this.contact.username);
  }

  createTicket(): void {
    this.commonRepository.createTicket(this.contact.username);
  }

  startAudioCall(): void {
    if (this.isLoggedInUser) {
      this.toastService.show("CAN_NOT_CALL_YOURSELF");
      return;
    }
    this.commonRepository.makeTalkAudioChatVideoOperation(this.contact.jid, "audio-call", "group-audio");
  }

  startVideoCall(): void {
    if (this.isLoggedInUser) {
      this.toastService.show("CAN_NOT_CALL_YOURSELF");
      return;
    }
    this.commonRepository.makeTalkAudioChatVideoOperation(this.contact.jid, "video-call", "group-video");
  }

  startChat(): void {
    if (this.isLoggedInUser) {
      this.toastService.show("CAN_NOT_CHAT_YOURSELF");
      return;
    }
    this.commonRepository.makeTalkAudioChatVideoOperation(this.contact.jid, "start-chat", "group-chat");
  }

  underDevelopment() {
    this.toastService.show("COMING_SOON");
  }

  openRecentActivity(item) {
    if (item.type === "talk") {
      if (item.chatType === "chat") {
        this.commonRepository.jumpToChat(this.contact, item.talkId, item.createdDt);
      } else {
        this.underDevelopment();
      }
    } else if (item.type === "mail") {
      console.log("openRecentActivity item: ", item);
      this.commonRepository.openMail(item.mailFolderID, item.id);
      this.closeSidebar();
    } else if (item.type === "task") {
      this.commonRepository.openTask(item.id);
    } else {
      this.underDevelopment();
    }
  }

  onSelectedTabChangeActivity(active) {
    this.isLoading = true;
    this.activeIndex = active;
    this.searchControl?.reset();
    if (this.activeIndex === 0) this.callSearchActivity("talk,mail");
    else if (this.activeIndex === 1) this.callSearchActivity("talk");
    else if (this.activeIndex === 2) this.callSearchActivity("mail");
  }

  async callSearchActivity(apps: string, keyword?: string) {
    if (!this.isLoading) {
      this.isLoading = true;
    }
    console.log("callSearchActivity", this.contact, this.userJID);
    if (!!this.searchResponse) {
      this.searchResponse.docs = [];
    }
    if (this.contact.jid || this.contact.emails && this.contact.emails.length > 0) {
      this.searchParams.from_s = (this.contact.jid) ? this.contact.jid : this.contact.emails[0].email;
      const email = this.searchParams.from_s;
      const username = email.split("@")[0];
      this.otherUserName = await this.mailService.getNameByEmail(username);
      this.searchParams.apps = apps;
      this.searchParams.q = keyword || "*:*";
      if (apps.includes("talk") && apps.includes("mail")) {
          this.searchParams.apps = "talk";
          this.commonRepository.searchDocs(this.searchParams).pipe(take(1)).subscribe(talk => {
            this.searchParams.apps = "mail";
            this.commonRepository.searchDocs(this.searchParams).pipe(take(1)).subscribe(mail => {
              this.isLoading = false;
              if (talk && talk.docs && talk.docs.length > 0 && mail && mail.docs && mail.docs.length > 0) {
                this.searchResponse.docs = [...talk.docs, ...mail.docs];
              }
              if (talk && talk.docs && talk.docs.length > 0 && (!mail || !mail.docs || mail.docs.length === 0)) {
                this.searchResponse.docs = talk.docs;
              }
              if (mail && mail.docs && mail.docs.length > 0 && (!talk || !talk.docs || talk.docs.length === 0)) {
                this.searchResponse.docs = mail.docs;
              }
              this.searchResponse.docs.sort((a, b) => {
                return new Date(b?.createdDt).getTime() - new Date(a?.createdDt).getTime();
              });
              this.mapSearchDocs();
              this.changeDetectionRef.detectChanges();
            });
        });
      } else {
        this.commonRepository.searchDocs(this.searchParams).pipe(take(1)).subscribe(docs => {
          this.isLoading = false;
          this.searchResponse = docs;
          this.searchResponse.docs.sort((a, b) => {
            return new Date(b?.createdDt).getTime() - new Date(a?.createdDt).getTime();
          });
          this.mapSearchDocs();
          this.changeDetectionRef.detectChanges();
        });
      }
    } else {
      if (this.contact.email) {
        this.otherUserName = await this.mailService.getNameByEmail(this.contact.first_name);
        this.searchParams.from_s = this.contact.email;
        this.searchParams.apps = apps;
        this.searchParams.q = keyword || "*:*";
        this.commonRepository.searchDocs(this.searchParams).pipe(take(1)).subscribe(docs => {
          this.isLoading = false;
          this.searchResponse = docs;
          this.mapSearchDocs();
          this.changeDetectionRef.detectChanges();
        });
      }
    }
  }

  mapSearchDocs() {
    this.searchResponse.docs.map(doc => {
      this.processMessageBody(doc).then((res: any) => {
          doc.contentTxt = this.getInnerText(res.changingThisBreaksApplicationSecurity);
      });
    });
  }

  getInnerText(htmlString) {
    let element = document.createElement("div");
    element.innerHTML = htmlString;
    return this.extractTextContent(element);
  }

  extractTextContent(element) {
    let text = "";
    for (let node of element.childNodes) {
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent.trim() + " ";
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            text += this.extractTextContent(node);
        }
    }
    return text.trim();
  }


  showFeatureUnderDevelopmentToast(): void {
    this.toastService.show("FEATURE_NEXT_VERSION_LBL");
  }

  private processMessageBody(message: any, skipEmoji?: boolean, processOnly?: boolean) {
    let messageBody = message?.contentTxt || "";
    try {
        if (message.htmlBody) {
            messageBody = message.htmlBody;
        } else if (message.html && message.html.body) {
            messageBody = message.html.body;
        }
        if (!!messageBody) {
            messageBody = messageBody.trim();
            if (/^:[a-zA-Z0-9-_+]+:$/g.test(messageBody)) {
                this.emojiOnly = true;
            }
        }

    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("[processMessageBody]", error);
    }

    return new Promise(async (resolve) => {
        const references = message.references;
        let isTicketMention = false;

        if (messageBody.startsWith("#quickreply_")) {
            const additionalActionsTranslationKeys = ["QUICK_REPLY_CANT_TALK_WHATS_UP", "QUICK_REPLY_CALL_YOU_RIGHT_BACK", "QUICK_REPLY_CALL_YOU_LATER", "QUICK_REPLY_CANT_TALK_CALL_ME_LATER"];
            let additionActionTranslations = {};
            this.translate.get(additionalActionsTranslationKeys).pipe(take(1)).subscribe(v => {
                additionActionTranslations = v;
            });
            try {
                const translateKey = messageBody.split("quickreply_")[1].trim();
                if (additionalActionsTranslationKeys.indexOf(translateKey) > -1) {
                    messageBody = additionActionTranslations[translateKey];
                    message.cachedContent = additionActionTranslations[translateKey];
                }
            } catch (error) {

            }
        }

        if (!message.forwardMessage) {
            let translations: any = {};
            this.translate.get(["TICKET_MENTION_RECEIVER_MESSAGE", "TICKET_MENTION_SENDER_MESSAGE", "META_TASK_MENTION_RECEIVER_MESSAGE", "META_TASK_MENTION_SENDER_MESSAGE"]).pipe(take(1)).subscribe(v => {
                translations = v;
            });
            isTicketMention = CommonUtils.parseRedmineMentions(TICKET_MENTION, messageBody).length > 0 || CommonUtils.parseRedmineMentions(META_TASK_MENTION, messageBody).length > 0;
            try {
                if (CommonUtils.parseRedmineMentions(TICKET_MENTION, messageBody).length > 0) {
                    const textToReplace = CommonUtils.parseRedmineMentions(TICKET_MENTION, messageBody)[0];
                    let userjid = textToReplace.split("#")[1];
                    let mentionText = translations.TICKET_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.otherUserName);
                    if (this.userJID?.email !== userjid) {
                        mentionText = translations.TICKET_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.otherUserName );
                    }
                    isTicketMention = true;
                    messageBody = messageBody.replace(textToReplace, mentionText).replace(/&lt;/g, "<").replace(/&gt;/g, ">");
                }
            } catch (error) {

            }
        }
        if (messageBody.includes("You are mentioned in a comment of this topic:")) {
          let translations: any = {};
          this.translate.get(["TOPIC_MENTION_MESSAGE_RECEIVER", "TOPIC_MENTION_MESSAGE_SENDER"]).pipe(take(1)).subscribe(v => {
              translations = v;
          });

          if (messageBody.includes(this.contact.email) ) {
              let textReplace = messageBody.split("You are mentioned in a comment of this topic:")[1];
              const userName = await this.mailService.getNameByEmail(this.contact.email);
              let mentionText = translations.TOPIC_MENTION_MESSAGE_SENDER.replace("{{ USERNAME }}", userName) ;
              messageBody = mentionText + textReplace;
          } else {
            let textReplace = messageBody.split("You are mentioned in a comment of this topic:")[1];
            let mentionText = translations.TOPIC_MENTION_MESSAGE_RECEIVER ;
            messageBody = mentionText + textReplace;
          }

        }
        if (!!message.cachedContent && !this.isSearchResult && !skipEmoji && !isTicketMention) {
            messageBody = message.cachedContent;
        } else {
            messageBody = CommonUtils.generateCachedBody(messageBody, this.isSearchResult ? this.keyword : null, skipEmoji);
            if (!this.isSearchResult && !skipEmoji) {
                const newMsg = { ...message, cachedContent: messageBody };

                // this.conversationRepo.getSelectedConversation().pipe(filter(res => !!res), take(1)).subscribe(selectedConversation => {
                //     const convTarget = !!message.convTarget ? message.convTarget : selectedConversation.Target;
                //     this.conversationRepo.updateMessage(newMsg, convTarget);
                // });

            }
        }

        if (message.group_action && (message.group_action === "ADD_PARTICIPANTS"
            || message.group_action.type === "ADD_PARTICIPANTS")) {
            if (message.group_action.data) {
                const participants = message.group_action.data.split(",");
                messageBody = messageBody.split("<br>")[1];
                if (!messageBody) { // e.g. when add a new user to omemo chat
                    messageBody = "";
                    participants.forEach((pJid, index) => {
                        const pDisplayName = pJid.split("@")[0].replace(".", " ");
                        messageBody += pDisplayName;
                        if (index !== participants.length - 1) {
                            messageBody += ", ";
                        }
                    });
                }
                this.totalParticipants = participants.length;
            } else {
                const firstLine = messageBody.split("<br>")[0];
                messageBody = messageBody.split("<br>")[1];
                if (!messageBody) { // e.g. when add a new user to omemo chat
                    this.totalParticipants = 1;
                    messageBody = "";
                } else {
                  this.totalParticipants = parseInt(firstLine.replace(/\D+/g, ""), 10);
                }
            }
            this.actorName = "this.contactRepo.getFullName(this.fromJid)";
            this.translationKey = this.totalParticipants > 1 ? "USER_ADD_N_PARTICIPANTS" : "USER_ADD_N_PARTICIPANT";
        } else if (message.group_action && (message.group_action === "UPDATE_AVATAR"
            || message.group_action.type === "UPDATE_AVATAR")) {
            this.fullActorName = messageBody.replace("has updated the group photo", "").replace("hat das Foto der Gruppe aktualisiert", "");
            this.translationKey = "HAS_UPDATED_GROUP_PHOTO";
        } else if (message.group_action && (message.group_action === "UPDATE_ENCRYPTION"
            || message.group_action.type === "UPDATE_ENCRYPTION" || (!!message.group_action.type.type && message.group_action.type.type === "UPDATE_ENCRYPTION"))) {
            this.translationKey = "ENABLED_E2E_ENCRYPTION";
            if (messageBody.indexOf("disabled E2E encryption for the chat") !== -1) {
                this.translationKey = "DISABLED_E2E_ENCRYPTION";
            }
            this.fullActorName = messageBody.replace("enabled E2E encryption for the chat", "").replace("disabled E2E encryption for the chat", "");
        } else if (message.group_action && (message.group_action === "created"
            || message.group_action.type === "created")) {
            // this.logger.info("groupCreatedCheck: ", message);
            if (!!message.convTarget) {
                if (message.convTarget.indexOf("talk_meeting@conference") > -1) {
                    this.isMeeting = true;
                }
            }
            // this.translationParam = { groupName: messageBody.replace(/Group\s"/, "").replace(/"\sCreated/, "")};
        } else if (!!message.group_action && typeof message.group_action?.type === "string" && message.group_action?.type?.indexOf("MISSED_CALL") !== -1) {
            try {
                const data = JSON.parse(message.group_action?.type);
                let missCallJids = data.jids;
            } catch (ex) {
                // this.logger.sentryErrorLog("parse group_action error", ex);
            }
        } else if (message.group_action && message.group_action.type === "JOINED_PARTICIPANT") {
            const joinee = message.body.split(" ")[0];
            this.fullActorName = "this.contactRepo.getFullName(joinee)";
            if (joinee === this.userJID?.email) {
                this.translationKey = "YOU_JOINED_THE_GROUPCHAT";
            } else {
                this.translationKey = "JOINED_THE_GROUPCHAT";
            }
        }

        if (message.forwardMessage) {
            let translations: any = {};
            this.translate.get(["TICKET_MENTION_RECEIVER_MESSAGE", "TICKET_MENTION_SENDER_MESSAGE", "META_TASK_MENTION_RECEIVER_MESSAGE", "META_TASK_MENTION_SENDER_MESSAGE"]).pipe(take(1)).subscribe(v => {
                translations = v;
            });
            try {
                if (CommonUtils.parseRedmineMentions(TICKET_MENTION, messageBody).length > 0) {
                    const textToReplace = CommonUtils.parseRedmineMentions(TICKET_MENTION, messageBody)[0];
                    let userjid = textToReplace.split("#")[1];
                    // "this.contactRepo.getFullName(message.forwardMessage.from)"
                    let mentionText = translations.TICKET_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.otherUserName);
                    if (this.userJID?.email !== userjid) {
                        mentionText = translations.TICKET_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.otherUserName );
                    }
                    messageBody = messageBody.replace(textToReplace, mentionText);
                } else if (CommonUtils.parseRedmineMentions(META_TASK_MENTION, messageBody).length > 0) {
                    const textToReplace = CommonUtils.parseRedmineMentions(META_TASK_MENTION, messageBody)[0];
                    let userjid = textToReplace.split("#")[1];
                    let mentionText = translations.TICKET_MENTION_RECEIVER_MESSAGE.replace("{{ sender_name }}", this.otherUserName);
                    if (this.userJID?.email !== userjid) {
                        mentionText = translations.TICKET_MENTION_SENDER_MESSAGE.replace("{{ receiver_name }}", this.otherUserName );
                    }
                    messageBody = messageBody.replace(textToReplace, mentionText);
                }
            } catch (error) {

            }
            if (!processOnly) {
                messageBody = this.sanitizer.bypassSecurityTrustHtml(messageBody.replace(/<a href=/g, "<a target=\"_blank\" class=\"open-new-window\" href=")
                    .replace(/<a class="([a-z\s0-9]*)"\shref=/g, "<a target=\"_blank\" class=\"open-new-window\" href="));
            }
        }

        if (messageBody.includes("http://") || messageBody.includes("https://")) {
        }
        if (!skipEmoji) {
            messageBody = this.sanitizer.bypassSecurityTrustHtml(messageBody.replace(/<a href=/g, "<a target=\"_blank\" class=\"open-new-window\" href=")
                .replace(/<a class="([a-z\s0-9]*)"\shref=/g, "<a target=\"_blank\" class=\"open-new-window\" href="));
            this.changeDetectionRef.markForCheck();
        }
        resolve(messageBody);
    });
  }

  isSameMonth(date: any): boolean {
    const isSame = moment(new Date(date)).isSame(new Date(), "month");
    return isSame;
  }

  isCalendarCheck(folderId: string): boolean {
    let check: boolean = false;
    const allFolders = [...this.calendarFolders, ...CalenderUtils.getChildFolders(this.calendarFolders)];
    allFolders.map(f => {
        if (f.f && f.f.indexOf("#") !== -1) {
            check = true;
        }
    });
    return check;
  }

  mobileGroupingEvents(calendarEvent: CalendarAppointment[]): void {
    const now = moment();
    const today = moment().startOf("day");
    const next7Days = moment().add(7, "days").endOf("day");

    const filteredEvents = calendarEvent.filter(event => {
      const start = moment(event.start);
      const end = moment(event.end);

      const isWithinRange = start.isBetween(today, next7Days, undefined, "[]");

      const isToday = start.isSame(now, "day");
      const isOngoingOrUpcoming = !isToday || end.isAfter(now);

      return isWithinRange && isOngoingOrUpcoming;
    });

    const grouping = _.groupBy(filteredEvents, element => moment(new Date(element.start)).startOf("day"));
    const sections = _.map(grouping, (items, start) => ({
      date: start,
      data: items
    }));
    const montGrouping =  _.groupBy(sections, element => moment(new Date(element.date)).startOf("month"));
    const eventGroupMap = _.map(montGrouping, (items, date) => ({
      headingDate: date,
      data: items
    }));
    this.mobileEvents = eventGroupMap;
    this.getMembersList();
    this.changeDetectionRef.markForCheck();
  }
  checkIfIdExist(id: any): boolean {
    return this.recussingEventPreventHitForGetparticipentsList.includes(id);
  }

  checkInst(event): boolean {
    if (event?.inst?.length) {
      const filterInst = event?.inst.filter(i => i?.fba);
      if (filterInst.length) return true;
    }
    return false;
  }

  getMembersList() {

    if (this.mobileEvents && this.mobileEvents.length > 0) {
      this.mobileEvents[0].data.map(dd => {
        dd.data.map(insideData => {
            const apptId = insideData.apptId;
            if (this.checkIfIdExist(apptId)) {
                // ID already processed, skip API call
                return;
            }
            // Mark as processed before making API call to avoid race condition
            this.recussingEventPreventHitForGetparticipentsList.push(apptId);
            const body = {
                id: insideData.invId,
                ridz: insideData.inst[0].ridZ
            };

            this.commonService.getMsgRequest(body).pipe(take(1)).subscribe(resp => {
                insideData["participants"] = resp.m[0]?.inv[0]?.comp[0]?.at;
                if (resp.m[0]?.inv[0]?.comp[0]?.at?.length > 0) {
                    this.havemultipleparticipants.push(apptId);
                }
                this.changeDetectionRef.markForCheck();
            });
        });
      });

    } else {
      setTimeout(() => {
        this.getMembersList();
      }, 250);
    }
  }

  haveMultipleParticipents(ev) {
    return this.havemultipleparticipants.includes(ev.apptId);
  }

  isToday(date: any): boolean {
    const today = new Date();
    const givenDate = new Date(date);

    return today.toDateString() === givenDate.toDateString();
  }

  // isCalendarCheck(folderId: string): boolean {
  //   let check: boolean = false;
  //   const allFolders = [...this.calendarFolders, ...CalenderUtils.getChildFolders(this.calendarFolders)];
  //   allFolders.map(f => {
  //       if (f.f && f.f.indexOf("#") !== -1) {
  //           check = true;
  //       }
  //   });
  //   return check;
  // }

    timeFormat(date, withAmPm = false): string {
      const DateString = new Date(date);

      let language: string = "en";
      const localLang = this.electronService.isElectron
          ? this.electronService.getFromStorage(MailConstants.MAIL_LANGUAGE)
          : localStorage.getItem(MailConstants.MAIL_LANGUAGE);
      if (localLang !== null && localLang !== undefined && localLang !== "undefined") {
        language = localLang;
      }
      language = language.match(/en|ru|de/) ? language : "en";
      if (language === "de") {
        return this.datepipe.transform(DateString, "HH:mm");
      }

      if (withAmPm) {
        return this.datepipe.transform(DateString, "hh:mm a");
      }
      return this.datepipe.transform(DateString, "hh:mm");
    }

    getBackgroundColor(color: string): string {
        const grandient = CalenderUtils.getBackgroundGrandientColor(color);
        if (!!grandient) {
          return grandient;
        }
        return color;
    }

    getTagColorByNames(names: string = ""): string {
      let color = MailConstants.DEFAULT_COLOR;
      if (names) {
        const tagNames = names.split(",");
        const tags = this.allZimbraTags.filter(tt => tt.name.toLowerCase() === tagNames[0].toLowerCase());
        if (!!tags && !!tags[0]) {
          const t = tags[0];
          if (t.color && MailConstants.COLOR_LIST[t.color]) {
            t.color = MailConstants.COLOR_LIST[t.color];
          }
          if (t.rgb) {
            t.rgb = t.rgb.toLowerCase();
            if (t.rgb === "#999") {
              t.rgb = "#999999";
            }
          }
          color = t.rgb || t.color || color;
        }
      }
      return color;
    }
    appointmentClick(event) {
      this.commonRepository.rightSidebarAppClick$.next(event);
    }

    openAvatarDialog() {
      if (this.commonRepository.showNoInternetToastIfRequired()) {
        return;
      }
      const dialogArgs = {
        autoFocus: false,
        width: "650px",
        height: "485px",
        maxWidth: "100vw",
        data: { userProfile: this.contact },
        panelClass: "mail__dialog"
      };
      const dialog = this.matDialog.open(AvatarUploadDialogComponent, dialogArgs);
      dialog.afterClosed().pipe(take(1)).subscribe((res: any) => {
        if (res) {
          this.contact.avatar = res;
          this.currentUser.imageData = res;
          this.commonService.avatarUpdated$.next(res);
          const currentTime = new Date().getTime();
          this.store.dispatch(new SetLastPhotoUpdate({email: this.currentUser.email, timestamp: currentTime}));
          this.changeDetectionRef.markForCheck();
        }
      });
    }

    displayTime(time) {
      const formatStr = "DD.MM.YYYY . HH:mm";
      return format(new Date(time), formatStr);
    }
}
